import axios from 'axios';
import {
  QueryStatus, useMutation, useQuery, useQueryClient,
} from 'react-query';
import { NIL } from 'uuid';
import { User, getEmptyUserInfo } from '../models/user';

const getUserById = async (userId: string) => {
  const apiUrl = process.env.REACT_APP_BASIX_API || '';
  const { data } = await axios.get(`${apiUrl}user/${userId}`);
  return data;
};

const postUser = async (user: User) => {
  const apiUrl = process.env.REACT_APP_BASIX_API || '';
  // return the saved record so callers can read the (possibly new) id — item 316 saves
  // the user's commission rates right after a create.
  if (user.id !== NIL) {
    const { data } = await axios.post(`${apiUrl}user/${user.id}`, user);
    return data;
  }
  const { data } = await axios.post(`${apiUrl}user`, user);
  return data;
};

interface UserProps {
  id: string;
  onSuccess?: (data: User) => void;
}
const useUser = (props: UserProps): {
  data: any,
  status: QueryStatus,
  error: unknown,
  setUser: (data: User) => Promise<unknown>
} => {
  const queryClient = useQueryClient();
  const { status, data, error } = useQuery(['user', props.id], () => getUserById(props.id), {
    onSuccess: (results) => {
      if (props.id === '' || props.onSuccess === undefined) return;
      props.onSuccess(results);
    },
    // item 246 — a 404 means this user id isn't in the DB anymore (e.g. removed by a
    // DEV DATA wipe) but a stale cached list still shows it. Drop the ghost: refresh the
    // users list so it reflects the real DB.
    onError: (err: any) => {
      if (err?.response?.status === 404) queryClient.invalidateQueries('users');
    },
    enabled: props.id !== '' && props.id !== NIL,
    refetchOnWindowFocus: false,
    // item 1084b — this feeds useCurrentUser, the app's IDENTITY. On a fresh boot (and
    // impersonation always boots fresh) the first attempt can fire BEFORE the axios
    // token interceptor registers → tokenless 401 — and with retry:false that single
    // miss read as "broken session" and killed the impersonation. Retry transient
    // failures (the interceptor is registered by then); a 404 stays fast-fail (the
    // ghost-row case above).
    retry: (failureCount: number, err: any) => err?.response?.status !== 404 && failureCount < 2,
  });

  const mutation = useMutation((newData: User) => postUser(newData), {
    onSuccess: async (response, variables) => {
      await queryClient.invalidateQueries('users');
      await queryClient.invalidateQueries(['user', variables.id]);
    },
  });

  const setUser = (userInfo: User) => mutation.mutateAsync(userInfo);

  if (props.id === NIL) {
    return {
      data: getEmptyUserInfo(), setUser, status: 'success', error: null,
    };
  }
  return {
    data, setUser, status, error,
  };
};

export default useUser;
